import { TranslationKey } from './../../../../../types/translation/TranslationKey';

import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import TextInput from '../../../Common/TextInput';
import { ProductTransaction } from '../../../../../types/productTransaction';
import { t } from '../../../../../types/translation/Translator';
import { useNavigate } from 'react-router-dom';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { UserContext } from '../../../../../context/UserContext';
import ProcessOrderTransactionModal from '../Modals/ProcessOrderTransactionModal';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { productTransactionTableHeaders } from '../../../Common/ProductTransactionTable';
import { BinContext } from '../../../../../context/BinContext';
import { LotContext } from '../../../../../context/LotContext';
import { ContainerContext } from '../../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../../context/ContainerTypeContext';
import Paper from '../../../../../VentoryUI/components/common/Paper';
import FlexPane from '../../../../../VentoryUI/components/common/FlexPane';
import Table from '../../../../components/Table';
import { OrderInput, OrderUtil } from '../../../../../types/order';
import { TableSettingsContext } from '../../../../../context/TableSettingsContext';
import { EntityType } from '../../../../../types/comment';
import { OrderProductTransactionTableSettingsColumn } from '../../../../../types/table_settings/productTransaction/OrderProductTransactionTableSettings';
import { FlowType } from '../../../../../types/order/orderFlow';
import { System } from '../../../../../types/system';

interface OrderDetailPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  footer: () => JSX.Element;
}

export default function OrderDetailPaneProps({ order, setOrder, footer }: OrderDetailPaneProps) {
  const navigate = useNavigate();

  const { stockLocations } = useContext(StockLocationContext);
  const { bins } = useContext(BinContext);
  const { lots } = useContext(LotContext);
  const { productTransactions, productTransactionsLoading } = useContext(ProductTransactionContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { currentCompany } = useContext(CompanyContext);
  const { containers, containersLoading } = useContext(ContainerContext);
  const { containerTypes, containerTypesLoading } = useContext(ContainerTypeContext);

  const productTransactionTableSettings = getTableSettings(
    EntityType.productTransaction,
    currentCompany.id,
    EntityType.order,
  );

  const [selected, setSelected] = useState<ProductTransaction | null>(null);
  const [openProcessModal, setOpenProcessModal] = useState<boolean>(false);

  const containerBased = !currentCompany.settings.featureToggles.orders.productBasedApproach;

  const orderTransactions = useMemo(() => {
    return [...productTransactions.values()]
      .flat()
      .filter(p => p.parentId === order.id && (containerBased ? !!p.product.containerId : !p.product.containerId))
      .sort((a, b) => {
        const statusCompare = Object.keys(System.ProductTransactionStatus)
          .indexOf(a.statusId)
          .toString()
          .localeCompare(Object.keys(System.ProductTransactionStatus).indexOf(b.statusId).toString());
        if (statusCompare !== 0) return statusCompare;

        const pmd = productMasterData.get(a.product.pmdId || '');
        const pmd2 = productMasterData.get(b.product.pmdId || '');
        const kitManagedCompare = (pmd2?.kitManaged ? '1' : '0').localeCompare(pmd?.kitManaged ? '1' : '0');
        if (kitManagedCompare !== 0) return kitManagedCompare;

        const containerCompare = (a.product.containerId || '').localeCompare(b.product.containerId || '');
        if (containerCompare !== 0) return containerCompare;

        const pmdCompare = (a.product.pmdId || '').localeCompare(b.product.pmdId || '');
        if (pmdCompare !== 0) return pmdCompare;

        const lotCompare = (a.product.lotId || '').localeCompare(b.product.lotId || '');
        if (lotCompare !== 0) return lotCompare;

        return 0;
      });
  }, [productTransactions]);

  return (
    <>
      {selected ? (
        <ProcessOrderTransactionModal
          disabled={false}
          open={openProcessModal}
          transaction={selected}
          setOpen={v => {
            setOpenProcessModal(v);
            setSelected(null);
            setOrder(new OrderInput(order));
          }}
        />
      ) : null}

      <FlexPane
        header={
          <Paper>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextInput
                  disabled
                  dynamicUpdate
                  onChange={() => {}}
                  label={t(TranslationKey.order).singular.label}
                  value={order.number || '0'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  disabled
                  dynamicUpdate
                  onChange={() => {}}
                  label={t(TranslationKey.orderType).singular.label}
                  testId={testIds.type}
                  value={OrderUtil.typeToString(order.type)}
                />
              </Grid>
              <Grid item xs={6}>
                {OrderUtil.hasFromStockLocation(order) && (
                  <TextInput
                    disabled
                    dynamicUpdate
                    onChange={() => {}}
                    label={t(TranslationKey.sourceStockLocation).singular.upper}
                    testId={testIds.stockLocation}
                    value={stockLocations.get(order.fromStockLocationId || 'NULL')?.name || ''}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                {OrderUtil.hasToStockLocation(order) && (
                  <TextInput
                    disabled
                    dynamicUpdate
                    onChange={() => {}}
                    label={t(TranslationKey.destinationStockLocation).singular.upper}
                    testId={testIds.stockLocation}
                    value={stockLocations.get(order.toStockLocationId || 'NULL')?.name || ''}
                  />
                )}
              </Grid>
            </Grid>
          </Paper>
        }
        content={
          <Table
            title={t(TranslationKey.transactions).singular.label}
            loading={
              productTransactionsLoading || productMasterDataLoading || containersLoading || containerTypesLoading
            }
            items={orderTransactions}
            onClick={item => {
              setSelected(item);
              setOpenProcessModal(true);
            }}
            headers={productTransactionTableHeaders(
              productTransactionTableSettings,
              stockLocations,
              bins,
              productMasterData,
              lots,
              companyUsers,
              containers,
              containerTypes,
              locale,
            ).filter(h => {
              switch (order.type) {
                case FlowType.inbound:
                case FlowType.move:
                  if (h.column === OrderProductTransactionTableSettingsColumn.sourceContainer) return false;
                  return true;
                case FlowType.outbound:
                case FlowType.disposal:
                case FlowType.move:
                  if (h.column === OrderProductTransactionTableSettingsColumn.destinationContainer) return false;
                  return true;
              }
              return true;
            })}
          />
        }
        footer={footer()}
      />
    </>
  );
}
